/* eslint-disable @typescript-eslint/no-explicit-any */

/** Normal PDF: f(x) = (1/(σ√(2π))) * exp(-(x-μ)²/(2σ²)) */
function normalPdf(x: number, mu: number, sigma: number): number {
  const coeff = 1 / (sigma * Math.sqrt(2 * Math.PI));
  const exponent = -((x - mu) ** 2) / (2 * sigma ** 2);
  return coeff * Math.exp(exponent);
}

/** Generate [x, y] data points for a normal PDF */
function generatePdfData(
  mu: number,
  sigma: number,
  xMin: number,
  xMax: number,
  numPoints = 300,
): Array<[number, number]> {
  const step = (xMax - xMin) / numPoints;
  const data: Array<[number, number]> = [];
  for (let i = 0; i <= numPoints; i++) {
    const x = xMin + i * step;
    data.push([parseFloat(x.toFixed(4)), parseFloat(normalPdf(x, mu, sigma).toFixed(6))]);
  }
  return data;
}

/** Round to 2 decimal places, stripping trailing zeros */
function fmt(n: number): string {
  return parseFloat(n.toFixed(2)).toString();
}

/** Build ECharts options for the two-curve chart */
export function buildChartOptions(muX: number, sigmaX: number, a: number, b: number): any {
  const muY = a * muX + b;
  const sigmaY = Math.abs(a) * sigmaX;
  const showTransformed = Math.abs(a) > 0.01;

  // Original curve always uses its own fixed range
  const origMin = muX - 4 * sigmaX;
  const origMax = muX + 4 * sigmaX;
  const originalData = generatePdfData(muX, sigmaX, origMin, origMax);

  // X-axis range: always include original, expand for transformed if needed
  let xMin = origMin - 1;
  let xMax = origMax + 1;

  let transformedData: Array<[number, number]> = [];
  if (showTransformed) {
    const transMin = muY - 4 * sigmaY;
    const transMax = muY + 4 * sigmaY;
    xMin = Math.min(origMin, transMin) - 1;
    xMax = Math.max(origMax, transMax) + 1;
    transformedData = generatePdfData(muY, sigmaY, transMin, transMax);
  }

  const GRAY = '#9ca3af';
  const BLUE = '#3b82f6';

  return {
    animation: false,
    grid: { left: 15, right: 15, top: 30, bottom: 35, containLabel: true },
    xAxis: {
      type: 'value',
      min: Math.floor(xMin),
      max: Math.ceil(xMax),
      axisLabel: { fontSize: 12, color: '#6b7280' },
      splitLine: { show: false },
      axisLine: { lineStyle: { color: '#d1d5db' } },
      axisTick: { lineStyle: { color: '#d1d5db' } },
    },
    yAxis: {
      type: 'value',
      show: false,
      min: 0,
    },
    tooltip: { show: false },
    series: [
      // Original curve — grey ghost (dashed, faded)
      {
        type: 'line',
        smooth: true,
        symbol: 'none',
        lineStyle: { color: GRAY, width: 2, type: 'dashed', opacity: 0.6 },
        areaStyle: { color: 'rgba(156, 163, 175, 0.06)' },
        data: originalData,
        markLine: {
          silent: true,
          symbol: ['none', 'none'],
          data: [
            {
              xAxis: muX,
              lineStyle: { color: GRAY, width: 1.5, type: 'dotted' },
              label: { show: false },
            },
          ],
        },
        markPoint: {
          symbol: 'circle',
          symbolSize: 0,
          label: { show: true },
          data: [
            {
              coord: [muX, normalPdf(muX, muX, sigmaX) + 0.01],
              label: {
                formatter: `X:  μ = ${fmt(muX)},  σ = ${fmt(sigmaX)}`,
                fontSize: 11,
                fontWeight: 'bold' as const,
                color: GRAY,
                backgroundColor: '#fff',
                padding: [2, 5],
                borderRadius: 3,
                position: 'left' as const,
                offset: [-10, 0],
              },
            },
          ],
        },
      },
      // Transformed curve — solid blue
      ...(showTransformed
        ? [
            {
              type: 'line' as const,
              smooth: true,
              symbol: 'none',
              lineStyle: { color: BLUE, width: 2.5 },
              areaStyle: { color: 'rgba(59, 130, 246, 0.12)' },
              data: transformedData,
              markLine: {
                silent: true,
                symbol: ['none', 'none'],
                data: [
                  {
                    xAxis: muY,
                    lineStyle: { color: BLUE, width: 1.5, type: 'dotted' as const },
                    label: { show: false },
                  },
                ],
              },
              markPoint: {
                symbol: 'circle',
                symbolSize: 0,
                label: { show: true },
                data: [
                  {
                    coord: [muY, normalPdf(muY, muY, sigmaY) + 0.01],
                    label: {
                      formatter: `${fmt(a)}X + ${fmt(b)}:  μ = ${fmt(muY)},  σ = ${fmt(sigmaY)}`,
                      fontSize: 11,
                      fontWeight: 'bold' as const,
                      color: BLUE,
                      backgroundColor: '#fff',
                      padding: [2, 5],
                      borderRadius: 3,
                      position: 'right' as const,
                      offset: [10, 0],
                    },
                  },
                ],
              },
            },
          ]
        : []),
    ],
  };
}
